import { useEffect, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useConfirm } from "@/components/ConfirmDialog";
import { fmtDateTime } from "@/lib/fmt";
import { fmtVal, recordTitle, LONG_TYPES } from "@/lib/records";
import RecordInput from "./RecordInput";

function FieldRow({ f, v, edit, onChange }) {
  const text = fmtVal(v);
  const long = LONG_TYPES.includes(f.type) || text.length > 120;
  return (
    <div className={long ? "col-span-full" : ""} data-testid={`record-field-${f.key}`}>
      <div className="text-[10px] uppercase tracking-wider text-[var(--text-faint)] mb-0.5">{f.label || f.key}</div>
      {edit ? <RecordInput f={f} value={v} onChange={onChange} /> : text ? (
        <div className={`text-sm whitespace-pre-wrap break-words ${long ? "leading-relaxed rounded border border-[var(--border)] bg-[var(--panel)] p-3 max-h-[480px] overflow-auto" : ""}`}>{text}</div>
      ) : <div className="text-sm text-[var(--text-faint)]">—</div>}
    </div>
  );
}

function groupFields(fields, d) {
  const known = new Set(fields.map((f) => f.key));
  const extra = Object.keys(d).filter((k) => !known.has(k)).map((k) => ({ key: k, label: k, section: "Other" }));
  const out = [];
  [...fields, ...extra].forEach((f) => {
    const s = f.section || "Details";
    let g = out.find((x) => x.s === s);
    if (!g) out.push((g = { s, fields: [] }));
    g.fields.push(f);
  });
  return out;
}

export default function RecordDrawer({ mod, record, fields, onClose, onSaved }) {
  const isNew = record && !record._id;
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState({});
  const { confirm } = useConfirm();
  useEffect(() => { setEdit(!!isNew); setDraft({ ...(record?.data || {}) }); }, [record, isNew]);
  const d = edit ? draft : record?.data || {};
  const save = async () => {
    const changed = Object.fromEntries(Object.entries(draft).filter(([k, v]) => JSON.stringify(v) !== JSON.stringify(record.data?.[k])));
    try {
      const r = isNew ? await api.post(`/crm/modules/${mod._id}/records`, { data: draft }) : await api.put(`/crm/modules/${mod._id}/records/${record._id}`, { data: changed });
      toast.success(isNew ? "Record created" : "Record saved");
      setEdit(false); onSaved(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const del = async () => {
    if (!(await confirm({ title: "Delete this record?", body: "This can't be undone.", danger: true }))) return;
    await api.delete(`/crm/modules/${mod._id}/records/${record._id}`);
    toast.success("Record deleted"); onSaved(null); onClose();
  };
  return (
    <Sheet open={!!record} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-[820px] bg-[var(--bg)] border-[var(--border-strong)] p-0 overflow-y-auto" data-testid="record-drawer">
        {record && (
          <div className="p-6 space-y-6">
            <div className="flex items-start gap-3 pr-8">
              <div className="flex-1 min-w-0">
                <SheetTitle className="text-xl font-medium break-words" data-testid="record-title">{isNew ? `New ${mod.name} record` : recordTitle(record, fields, mod.title_field)}</SheetTitle>
                {!isNew && <div className="text-xs text-[var(--text-dim)] mt-1">Updated {fmtDateTime(record.updated_at)} · created {fmtDateTime(record.created_at)}{record.created_by ? ` by ${record.created_by}` : ""}</div>}
              </div>
              {edit ? (
                <>
                  {!isNew && <button className="btn text-xs" onClick={() => { setEdit(false); setDraft({ ...record.data }); }} data-testid="record-cancel">Cancel</button>}
                  <button className="btn btn-primary text-xs" onClick={save} data-testid="record-save">Save</button>
                </>
              ) : (
                <>
                  <button className="btn text-xs" onClick={() => setEdit(true)} data-testid="record-edit"><Pencil size={12} />Edit</button>
                  <button className="btn text-xs hover:text-[var(--breach)]" onClick={del} data-testid="record-delete"><Trash2 size={12} /></button>
                </>
              )}
            </div>
            {groupFields(fields, d).map((g) => (
              <section key={g.s} data-testid={`record-section-${g.s}`}>
                <h3 className="text-xs uppercase tracking-wider text-[var(--text-dim)] border-b border-[var(--border)] pb-1 mb-3">{g.s}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                  {g.fields.map((f) => <FieldRow key={f.key} f={f} v={d[f.key]} edit={edit} onChange={(v) => setDraft((x) => ({ ...x, [f.key]: v }))} />)}
                </div>
              </section>
            ))}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
